import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { getPaginatedNonprofits } from "../../../api/Nonprofits";
import { urlifyName } from "../../shared/Utils/PageUrl";

// Logo component used in the nonprofit list
const NonprofitLogo = ({ nonprofit }) => {
  return (
    <div className="w-12 h-12 overflow-hidden rounded-full bg-black flex items-center justify-center">
      {nonprofit.logo ? (
        <img
          src={nonprofit.logo}
          alt={`${nonprofit.org_name} logo`}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="flex items-center justify-center w-full h-full">
          <svg viewBox="0 0 100 100" className="w-10 h-10">
            <circle cx="50" cy="50" r="40" fill="#000000" />
            <text
              x="50"
              y="55"
              fontSize="30"
              fontWeight="bold"
              textAnchor="middle"
              fill="#FFFFFF"
            >
              {nonprofit.org_name.charAt(0)}
            </text>
          </svg>
        </div>
      )}
    </div>
  );
};

const StateNonprofitsList = ({ selectedState }) => {
  const [nonprofits, setNonprofits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerStyle, setContainerStyle] = useState({
    height: "592px",
  });

  useEffect(() => {
    if (!selectedState) {
      setNonprofits([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    getPaginatedNonprofits(1, "", selectedState, 100)
      .then((response) => {
        setNonprofits(response.results || []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching nonprofits for state:", error);
        setError("Failed to load nonprofits for this state");
        setIsLoading(false);
      });
  }, [selectedState]);

  // Get full state name from the abbreviation
  const getStateName = (abbr) => {
    const stateNames = {
      AL: "Alabama",
      AK: "Alaska",
      AZ: "Arizona",
      AR: "Arkansas",
      CA: "California",
      CO: "Colorado",
      CT: "Connecticut",
      DE: "Delaware",
      FL: "Florida",
      GA: "Georgia",
      HI: "Hawaii",
      ID: "Idaho",
      IL: "Illinois",
      IN: "Indiana",
      IA: "Iowa",
      KS: "Kansas",
      KY: "Kentucky",
      LA: "Louisiana",
      ME: "Maine",
      MD: "Maryland",
      MA: "Massachusetts",
      MI: "Michigan",
      MN: "Minnesota",
      MS: "Mississippi",
      MO: "Missouri",
      MT: "Montana",
      NE: "Nebraska",
      NV: "Nevada",
      NH: "New Hampshire",
      NJ: "New Jersey",
      NM: "New Mexico",
      NY: "New York",
      NC: "North Carolina",
      ND: "North Dakota",
      OH: "Ohio",
      OK: "Oklahoma",
      OR: "Oregon",
      PA: "Pennsylvania",
      RI: "Rhode Island",
      SC: "South Carolina",
      SD: "South Dakota",
      TN: "Tennessee",
      TX: "Texas",
      UT: "Utah",
      VT: "Vermont",
      VA: "Virginia",
      WA: "Washington",
      WV: "West Virginia",
      WI: "Wisconsin",
      WY: "Wyoming",
      DC: "District of Columbia",
    };
    return stateNames[abbr] || abbr;
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-[600px] lg:max-w-none lg:w-[320px] xl:w-[403px] mx-auto lg:mx-0 rounded-lg shadow-md bg-white overflow-hidden flex flex-col">
        <div className="bg-[#1B3A5D] text-white px-4 sm:px-6 py-3 sm:py-4 text-start font-bold text-lg sm:text-xl">
          {selectedState ? getStateName(selectedState) : "Select a State"}
        </div>
        <div
          className="animate-pulse flex flex-col p-3 sm:p-4"
          style={containerStyle}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="flex items-center space-x-3 sm:space-x-4 border-b border-gray-100 py-3 sm:py-4"
            >
              <div className="rounded-full bg-gray-200 h-10 w-10 sm:h-12 sm:w-12"></div>
              <div className="flex-1 space-y-2">
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-[600px] lg:max-w-none lg:w-[320px] xl:w-[403px] mx-auto lg:mx-0 rounded-lg shadow-md bg-white overflow-hidden flex flex-col">
        <div className="bg-[#1B3A5D] text-white p-3 sm:p-4 font-bold text-lg sm:text-xl">
          {selectedState ? getStateName(selectedState) : "Select a State"}
        </div>
        <div className="p-3 sm:p-4" style={containerStyle}>
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[600px] lg:max-w-none lg:w-[320px] xl:w-[403px] mx-auto lg:mx-0 rounded-lg shadow-md bg-white overflow-hidden flex flex-col">
      <div className="bg-[#1B3A5D] text-white px-4 sm:px-6 py-3 sm:py-4 text-start font-bold text-lg sm:text-xl">
        {selectedState ? getStateName(selectedState) : "Select a State"}
      </div>
      <div className="overflow-y-auto flex-grow h-full" style={containerStyle}>
        {nonprofits.length > 0 ? (
          nonprofits.map((nonprofit) => (
            <Link
              key={nonprofit.id}
              to={`/non-profit/${urlifyName(nonprofit.org_name)}/${
                nonprofit.id
              }`}
              className="flex items-center px-4 sm:px-6 md:px-8 py-3 sm:py-4 md:py-5 border-gray-100 hover:bg-[#F4F4F4] transition-colors"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 overflow-hidden rounded-xl bg-black flex items-center justify-center bg-cover">
                {nonprofit.logo ? (
                  <img
                    src={nonprofit.logo}
                    alt={`${nonprofit.org_name} logo`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <svg
                      viewBox="0 0 100 100"
                      className="w-8 h-8 sm:w-10 sm:h-10"
                    >
                      <circle cx="50" cy="50" r="40" fill="#000000" />
                      <text
                        x="50"
                        y="55"
                        fontSize="30"
                        fontWeight="bold"
                        textAnchor="middle"
                        fill="#FFFFFF"
                      >
                        {nonprofit.org_name.charAt(0)}
                      </text>
                    </svg>
                  </div>
                )}
              </div>
              <div className="ml-4 sm:ml-7">
                <div className="!text-xs !font-montserrat text-left">
                  {nonprofit.address?.city}, {nonprofit.address?.state}
                </div>
                <div className="font-bold !text-sm sm:!text-base !font-montserrat text-left text-black">
                  {nonprofit.org_name}
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="text-center !font-montserrat !font-medium py-6 sm:py-10">
            {selectedState
              ? `No nonprofits found in ${getStateName(selectedState)}`
              : "Select a state to view nonprofits"}
          </div>
        )}
      </div>
    </div>
  );
};

export default StateNonprofitsList;
